import {useEffect} from 'react'

type ConnectivityConfig = {
  enabled: boolean
  checkUrl: string
  intervalMs: number
  timeoutMs: number
}

type ConnectivityPeriod = {
  startedAt: string
  startedAtIso: string
  endedAt?: string
  endedAtIso?: string
  durationSeconds?: number
  url: string
}

declare global {
  interface Window {
    __CONNECTIVITY_MONITOR_CONFIG__?: ConnectivityConfig
  }
}

const DEFAULT_CONFIG: ConnectivityConfig = {
  enabled: false,
  checkUrl: 'https://www.gstatic.com/generate_204',
  intervalMs: 10000,
  timeoutMs: 5000,
}

const CONNECTIVITY_PERIODS_KEY = 'connectivity_monitor_periods'

const getCurrentDateTime = () => {
  const now = new Date()

  const date = now.toLocaleDateString('es-PE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })

  const time = now.toLocaleTimeString('es-PE', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })

  const milliseconds = String(now.getMilliseconds()).padStart(3, '0')

  return `${date} ${time}.${milliseconds}`
}

const getConnectivityPeriods = (): ConnectivityPeriod[] => {
  try {
    const periodsRaw = localStorage.getItem(CONNECTIVITY_PERIODS_KEY)
    return periodsRaw ? JSON.parse(periodsRaw) : []
  } catch {
    return []
  }
}

const saveConnectivityPeriods = (periods: ConnectivityPeriod[]) => {
  localStorage.setItem(CONNECTIVITY_PERIODS_KEY, JSON.stringify(periods))
}

const saveOfflineStart = (url: string) => {
  const periods = getConnectivityPeriods()
  const lastPeriod = periods[periods.length - 1]

  if (lastPeriod && !lastPeriod.endedAt) {
    return
  }

  const now = new Date()

  periods.push({
    startedAt: getCurrentDateTime(),
    startedAtIso: now.toISOString(),
    url,
  })

  saveConnectivityPeriods(periods)
}

const saveOnlineEnd = () => {
  const periods = getConnectivityPeriods()
  const lastPeriod = periods[periods.length - 1]

  if (!lastPeriod || lastPeriod.endedAt) {
    return
  }

  const now = new Date()
  const startedAt = new Date(lastPeriod.startedAtIso)

  lastPeriod.endedAt = getCurrentDateTime()
  lastPeriod.endedAtIso = now.toISOString()
  lastPeriod.durationSeconds = Math.round((now.getTime() - startedAt.getTime()) / 1000)

  saveConnectivityPeriods(periods)
}

const printConnectivityPeriods = () => {
  const periods = getConnectivityPeriods()

  if (periods.length === 0) {
    return
  }

  console.group('Historial de periodos sin conexión')
  console.table(
    periods.map((period, index) => ({
      nro: index + 1,
      inicio: period.startedAt,
      fin: period.endedAt ?? 'Aún sin conexión',
      duracionSegundos: period.durationSeconds ?? '-',
      url: period.url,
    })),
  )
  console.groupEnd()
}

const loadExternalScript = (src: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = src
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error(`No se pudo cargar ${src}`))

    document.head.appendChild(script)
  })
}

const fetchWithTimeout = async (url: string, timeoutMs: number) => {
  const controller = new AbortController()
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs)

  try {
    await fetch(`${url}?t=${Date.now()}`, {
      method: 'GET',
      mode: 'no-cors',
      cache: 'no-store',
      signal: controller.signal,
    })

    return true
  } catch {
    return false
  } finally {
    window.clearTimeout(timeoutId)
  }
}

export const ConnectivityMonitor = () => {
  useEffect(() => {
    printConnectivityPeriods()

    const root = document.documentElement
    let intervalId: number | undefined
    let wasOffline = false

    const loadConfigAndStart = async () => {
      let config = DEFAULT_CONFIG

      try {
        const configUrl = `${process.env.PUBLIC_URL}/connectivity.monitor.js?v=${Date.now()}`

        await loadExternalScript(configUrl)

        config = {
          ...DEFAULT_CONFIG,
          ...window.__CONNECTIVITY_MONITOR_CONFIG__,
        }
      } catch (error) {
        console.warn(
          'No se pudo cargar connectivity.monitor.js. Se usará configuración por defecto.',
          error,
        )

        config = DEFAULT_CONFIG
      }

      if (!config.enabled) {
        root.classList.remove('is-offline')
        return
      }

      const checkConnection = async () => {
        const hasInternet = await fetchWithTimeout(config.checkUrl, config.timeoutMs)

        if (!hasInternet) {
          root.classList.add('is-offline')

          if (!wasOffline) {
            wasOffline = true

            saveOfflineStart(config.checkUrl)

            console.warn(
              `[${getCurrentDateTime()}] Sin conexión a internet. URL verificada: ${
                config.checkUrl
              }`,
            )
          }

          return
        }

        root.classList.remove('is-offline')

        if (wasOffline) {
          wasOffline = false

          saveOnlineEnd()

          console.info(
            `[${getCurrentDateTime()}] Conexión restablecida. Se refrescará la página.`,
          )

          window.location.reload()
        }
      }

      await checkConnection()

      intervalId = window.setInterval(checkConnection, config.intervalMs)
    }

    loadConfigAndStart()

    return () => {
      if (intervalId) {
        window.clearInterval(intervalId)
      }
    }
  }, [])

  return null
}